import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Pencil, Trash2, X, AlertCircle, Search } from 'lucide-react';
import { supabase } from '../../supabase';
import {
  inputCls, labelCls, btnPrimary, btnSecondary, btnDanger,
  errorCls, pageTitleCls, pageSubtitleCls,
} from '../../components/ui';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface CicloCatalogo {
  id: number;
  codigo: string;
  nombre: string;
  observaciones?: string;
  activo: boolean;
  created_at?: string;
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children }: {
  title: string; onClose: () => void; children: React.ReactNode;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}>
      <div className="relative w-full flex flex-col rounded-xl shadow-2xl overflow-hidden"
        style={{
          maxWidth: '32rem', maxHeight: '90vh',
          background: 'var(--surface-overlay)', border: '1px solid var(--line)',
        }}>
        <div className="flex items-center justify-between px-5 py-3.5 shrink-0"
          style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{title}</span>
          <button onClick={onClose} style={{ color: 'var(--ink-faint)' }}><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// ─── Formulario ───────────────────────────────────────────────────────────────

function CicloForm({ initial, onSave, onCancel }: {
  initial?: CicloCatalogo;
  onSave: (d: Partial<CicloCatalogo>) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    codigo: initial?.codigo ?? '',
    nombre: initial?.nombre ?? '',
    observaciones: initial?.observaciones ?? '',
    activo: initial?.activo ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof typeof form, v: any) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.codigo.trim()) { setErr('El código es obligatorio.'); return; }
    if (!form.nombre.trim()) { setErr('El nombre es obligatorio.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({
        ...form,
        codigo: form.codigo.trim().padStart(3, '0'),
        nombre: form.nombre.trim(),
        observaciones: form.observaciones.trim() || undefined,
      });
    } catch (ex: any) { setErr(ex?.message ?? 'Error al guardar.'); setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Código *</label>
          <input className={inputCls} value={form.codigo}
            onChange={e => set('codigo', e.target.value)} placeholder="001" maxLength={10} />
        </div>
        <div className="col-span-2">
          <label className={labelCls}>Nombre *</label>
          <input className={inputCls} value={form.nombre}
            onChange={e => set('nombre', e.target.value)} placeholder="Foliar 1, Induccion Floral..." />
        </div>
      </div>
      <div>
        <label className={labelCls}>Observaciones</label>
        <input className={inputCls} value={form.observaciones}
          onChange={e => set('observaciones', e.target.value)} placeholder="Opcional..." />
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.activo} onChange={e => set('activo', e.target.checked)} />
        <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Ciclo activo</span>
      </label>
      {err && <p className={errorCls}>{err}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className={btnSecondary} onClick={onCancel}>Cancelar</button>
        <button type="submit" className={btnPrimary} disabled={saving}>
          {saving ? 'Guardando...' : initial ? 'Actualizar' : 'Crear Ciclo'}
        </button>
      </div>
    </form>
  );
}

// ─── Página Principal ─────────────────────────────────────────────────────────

export function CiclosCatalogoPage() {
  const [ciclos, setCiclos] = useState<CicloCatalogo[]>([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [soloActivos, setSoloActivos] = useState(true);
  const [modal, setModal] = useState<'create' | CicloCatalogo | null>(null);
  const [deleting, setDeleting] = useState<CicloCatalogo | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('app_ciclos_catalogo')
      .select('*')
      .order('codigo')
      .order('nombre');
    setCiclos(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = ciclos.filter(c => {
    if (soloActivos && !c.activo) return false;
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase();
    return c.codigo.includes(q) || c.nombre.toLowerCase().includes(q) ||
      (c.observaciones ?? '').toLowerCase().includes(q);
  });

  async function save(data: Partial<CicloCatalogo>) {
    if (modal && modal !== 'create') {
      const { error } = await supabase
        .from('app_ciclos_catalogo')
        .update(data)
        .eq('id', (modal as CicloCatalogo).id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await supabase
        .from('app_ciclos_catalogo')
        .insert(data);
      if (error) throw new Error(error.message);
    }
    setModal(null);
    await cargar();
  }

  async function toggleActivo(c: CicloCatalogo) {
    await supabase.from('app_ciclos_catalogo').update({ activo: !c.activo }).eq('id', c.id);
    await cargar();
  }

  async function confirmDelete() {
    if (!deleting) return;
    await supabase.from('app_ciclos_catalogo').delete().eq('id', deleting.id);
    setDeleting(null);
    await cargar();
  }

  const activos = ciclos.filter(c => c.activo).length;
  const inactivos = ciclos.filter(c => !c.activo).length;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 shrink-0 flex items-center justify-between gap-4"
        style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
        <div>
          <h1 className={pageTitleCls}>Catálogo de Ciclos</h1>
          <p className={pageSubtitleCls}>
            Tipos de aplicación — {activos} activos · {inactivos} inactivos · {ciclos.length} total
          </p>
        </div>
        <button className={btnPrimary} onClick={() => setModal('create')}>
          <span className="flex items-center gap-1.5"><Plus size={14} /> Nuevo Ciclo</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="px-5 py-3 shrink-0 flex items-center gap-3"
        style={{ borderBottom: '1px solid var(--line-dim)', background: 'var(--surface-base)' }}>
        <div className="relative flex-1 max-w-xs">
          <input
            className={inputCls}
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por código o nombre..."
          />
          <Search size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: 'var(--ink-faint)' }} />
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-xs" style={{ color: 'var(--ink-muted)' }}>
          <input type="checkbox" checked={soloActivos} onChange={e => setSoloActivos(e.target.checked)} />
          Solo activos
        </label>
        <span className="text-xs ml-auto" style={{ color: 'var(--ink-faint)' }}>
          {filtrados.length} resultados
        </span>
      </div>

      {/* Tabla */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--emp-accent)' }} />
          </div>
        ) : filtrados.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-2 opacity-50">
            <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Sin resultados</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead style={{ position: 'sticky', top: 0, background: 'var(--surface-raised)', zIndex: 1 }}>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                {['Código', 'Nombre', 'Observaciones', 'Estado', ''].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: 'var(--ink-faint)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrados.map((c, i) => (
                <tr key={c.id}
                  style={{
                    borderBottom: '1px solid var(--line-dim)',
                    background: i % 2 === 0 ? 'transparent' : 'var(--surface-deep)',
                  }}>
                  <td className="px-4 py-2.5">
                    <span className="font-mono font-bold text-[11px]"
                      style={{ color: 'var(--emp-accent-txt)' }}>{c.codigo}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span style={{ color: c.activo ? 'var(--ink)' : 'var(--ink-faint)' }}>{c.nombre}</span>
                  </td>
                  <td className="px-4 py-2.5" style={{ color: 'var(--ink-faint)' }}>
                    {c.observaciones || '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => toggleActivo(c)}
                      className="px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors"
                      style={c.activo
                        ? { background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }
                        : { background: 'var(--surface-deep)', color: 'var(--ink-faint)', border: '1px solid var(--line)' }}>
                      {c.activo ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setModal(c)} className="p-1.5 rounded transition-colors"
                        style={{ color: 'var(--ink-faint)' }}
                        onMouseEnter={e => (e.currentTarget.style.color = 'var(--emp-accent-txt)')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => setDeleting(c)} className="p-1.5 rounded transition-colors"
                        style={{ color: 'var(--ink-faint)' }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modales */}
      {modal !== null && (
        <Modal
          title={modal === 'create' ? 'Nuevo Ciclo' : `Editar — ${(modal as CicloCatalogo).nombre}`}
          onClose={() => setModal(null)}>
          <CicloForm
            initial={modal !== 'create' ? (modal as CicloCatalogo) : undefined}
            onSave={save}
            onCancel={() => setModal(null)}
          />
        </Modal>
      )}

      {deleting && (
        <Modal title="Eliminar Ciclo" onClose={() => setDeleting(null)}>
          <div className="space-y-4">
            <div className="flex gap-3 p-3 rounded"
              style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
              <AlertCircle size={16} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
              <p className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>
                ¿Eliminar el ciclo <strong>{deleting.codigo} — {deleting.nombre}</strong>?
                Esta acción es permanente.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button className={btnSecondary} onClick={() => setDeleting(null)}>Cancelar</button>
              <button className={btnDanger} onClick={confirmDelete}>Eliminar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
